import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { cn } from './cn'
import { Slot } from './Slot'

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean
  /** Leading colour dot (any CSS colour). */
  dot?: string
  /** Leading icon. */
  leading?: ReactNode
  /** Emoji or icon after the label, the Skool way ("General 💬"). */
  trailing?: ReactNode
  /** Muted count suffix. */
  count?: number | string
  asChild?: boolean
  ref?: Ref<HTMLButtonElement>
}

/** Filter pill (h34, px14, 13/500): white with a 1px line; selected chips use the neutral gray fill. */
export function Chip({ selected, dot, leading, trailing, count, asChild, className, children, type, ref, ...rest }: ChipProps) {
  const classes = cn(
    'inline-flex h-[34px] max-sm:h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-meta font-medium',
    'transition-[color,background-color,border-color,box-shadow] duration-150 ease-out focus-ring select-none',
    'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4',
    selected
      ? 'border-chip-selected bg-chip-selected text-on-chip-selected'
      : 'border-line bg-surface text-muted hover:border-line-strong hover:bg-surface-2 hover:text-text',
    className,
  )
  const content = (
    <>
      {dot && <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: dot }} aria-hidden="true" />}
      {leading}
      <span>{children}</span>
      {trailing != null && trailing !== false ? (
        <span className="-ml-0.5 shrink-0 leading-none" aria-hidden={typeof trailing === 'string' ? true : undefined}>
          {trailing}
        </span>
      ) : null}
      {count !== undefined && (
        // Full white on the selected gray: at 80 percent the count was 3.7:1 (UXA-10); the lighter weight sets it apart.
        <span className={cn('font-normal tabular-nums', selected ? 'text-on-chip-selected' : 'text-subtle')}>{count}</span>
      )}
    </>
  )
  if (asChild) {
    return (
      <Slot className={classes} aria-current={selected ? 'true' : undefined} ref={ref as Ref<HTMLElement>} {...rest}>
        {children}
      </Slot>
    )
  }
  return (
    <button ref={ref} type={type ?? 'button'} aria-pressed={selected ?? false} className={classes} {...rest}>
      {content}
    </button>
  )
}
